<template lang="pug">
.chart
  Bar(:data="chartData" :options="chartOptions")
  .color-bar
    div(
      v-for="rating in ratingDisplayInfos"
      :key="rating.code"
      :style="{ backgroundColor: rating.color, width: `${rating.size}%` }"
    )
      .text(:style="{ color: pickTextOn(rating.color) }") {{ rating.code }}
  .legend
    .legend-item
      .color(:style="{ backgroundColor: getRatingColor(currentRating.code) }")
      .name
        span {{ currentRating.code }}
      | {{ $t('carbonFootprint.chart.before', { emissionBefore }) }}
    .legend-item
      .color(:style="{ backgroundColor: getRatingColor(newRating.code) }")
      .name
        span {{ newRating.code }}
      | {{ $t('carbonFootprint.chart.after', { emissionAfter }) }}
</template>
<script lang="ts">
import {PropType, defineComponent, ref, watch} from "vue";
import {
  BarElement, CategoryScale,
  Chart as ChartJS, LinearScale, Tooltip
} from 'chart.js';
import { Bar } from 'vue-chartjs';
import {
  BerRatings,
  getRatingColor,
  calculatePercentage,
  getBerMaxValue,
  getBerFirstValue,
} from "@/helpers";
import type { Rating } from "@/types";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {pickTextOn} from "@/helpers/font-contrast";
export default defineComponent({
  components: {
    Bar,
  },
  props: {
    currentRating: {
      type: Object as PropType<Rating>,
      required: true,
    },
    newRating: {
      type: Object as PropType<Rating>,
      required: true,
    },
    emissionBefore: {
      type: String,
      required: true,
    },
    emissionAfter: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    ChartJS.register(ChartDataLabels, BarElement, CategoryScale, LinearScale, Tooltip);
    const chartData = ref();
    const chartOptions = ref();
    const chart = ref();
    const chartValues = getChartValues(props.currentRating, props.newRating);
    const chartMaxValue = Math.max(getBerMaxValue(), ...chartValues) * 1.08;

    watch(() => props.newRating, () => {
      if (chart.value) chart.value.refresh();
    });

    chartData.value = {
      labels: [
        (props.currentRating as Rating).code,
        (props.newRating as Rating).code,
      ],
      datasets: [
        {
          label: "BER",
          data: chartValues,
          backgroundColor: [
            getRatingColor((props.currentRating as Rating).code, true),
            getRatingColor((props.newRating as Rating).code, true),
          ],
          borderColor: [
            getRatingColor((props.currentRating as Rating).code),
            getRatingColor((props.newRating as Rating).code),
          ],
          borderWidth: 2,
          barPercentage: 0.4,
        },
      ],
    };
    chartOptions.value = {
      indexAxis: "y",
      aspectRatio: 4,
      scales: {
        x: {
          ticks: {
            display: false,
          },
          grid: {
            display: false,
          },
          beginAtZero: true,
          min: 0,
          max: chartMaxValue,
        },
        y: {
          ticks: {
            display: false,
          },
          grid: {
            display: false,
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          align: "end",
          anchor: "end",
          color: function (context) {
            return context.dataset.borderColor;
          },
          font: function (context) {
            const w = context.chart.width;
            return {
              size: w < 512 ? 12 : 14,
              weight: "bold",
            };
          },
          formatter: function (_value, context) {
            return context.chart.data.labels[context.dataIndex];
          },
        },
        tooltip: {
          enabled: false,
        },
      },
    };

    window.addEventListener("resize", () => {
      if (chart.value) chart.value.reinit();
    });

    const ratingDisplayInfos = Array.from([...BerRatings.entries()]).map(
      ([key, item]) => {
        const widthPercentage = calculatePercentage(item.level);

        return {
          code: key,
          color: item.color,
          size: widthPercentage,
        };
      }
    );

    function getChartValues(currentRating: Rating, newRating: Rating) {
      const values = [
        Number(currentRating?.value) || 0,
        Number(newRating?.value) || 0,
      ];

      return getBerMaxValue() == getBerFirstValue()
        ? values.map((value) => getBerMaxValue() - value + 0.11)
        : values;
    }

    return {
      chartData,
      chartOptions,
      chart,
      BerRatings,
      getRatingColor,
      ratingDisplayInfos,
      pickTextOn,
    };
  },
});
</script>

<style scoped lang="scss">
.color-bar {
  width: 100%;
  height: 1.2rem;
  display: flex;
  padding-left: 0.5rem;

  div {
    &:has(.text) {
      min-width: max-content;
    }

    &:last-child {
      flex-grow: 1;
    }
  }

  .text {
    color: #fff;
    text-align: center;
    font-weight: 400;
    padding: 0 2px;
  }
}

.legend {
  margin-top: 2.5rem;
  display: flex;
  justify-content: center;
  width: 100%;
  gap: 2rem;
  flex-wrap: wrap;

  @media screen and (min-width: 640px) {
    gap: 5rem;
  }

  .legend-item {
    display: flex;
    gap: 0.5rem;
    align-items: center;

    .name {
      span {
        font-weight: bold;
      }
    }

    .color {
      width: 1rem;
      height: 1rem;
    }
  }
}

@media screen and (max-width: 640px) {
  .text {
    display: none;
  }
}
</style>
